/**
 * Loads the tenant list, resolves the active tenant from the URL, and applies
 * its brand to the chrome via CSS variables (the accent family: active nav,
 * primary buttons, focus, selection). Data colours are never branded.
 */

import { createContext, useContext, useEffect, useMemo } from "react";
import { useTenants } from "../api/hooks.js";
import { useFilters } from "../lib/useFilters.js";
import { ALL_TENANTS, TENANTS, getTenant, platformsInclude } from "../../shared/tenants.js";

const TenantContext = createContext(null);

/** [h 0–360, s 0–1, l 0–1] from "#rrggbb", or null. */
function hexToHsl(hex) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex ?? "");
  if (!m) return null;
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;
  let h = 0;
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  const s = d ? d / (1 - Math.abs(2 * l - 1)) : 0;
  return [(h * 60 + 360) % 360, s, l];
}

function hslToRgb([h, s, l]) {
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [f(0), f(8), f(4)];
}

const luminance = (rgb) => {
  const [r, g, b] = rgb.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a, b) => {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
};
const css = ([h, s, l]) => `hsl(${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`;

/** The darkest-needed shade of `hsl` (lightness lowered) that reaches 4.5:1 against `bg`. */
function readableOn(hsl, bg) {
  let [h, s, l] = hsl;
  while (l > 0.05 && contrast(hslToRgb([h, s, l]), bg) < 4.5) l -= 0.01;
  return [h, s, l];
}

/**
 * The accent family from a brand accent: a fill white text can sit on, its
 * hover, a pale tint for selected rows and pills, and ink that reads on the tint.
 * Brand accents vary (some are pastel), so every shade is contrast-checked.
 */
function accentShades(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  const [h, s] = hsl;
  const fill = readableOn(hsl, [1, 1, 1]);
  const soft = [h, Math.min(s, 0.75), 0.955];
  const ink = readableOn([h, s, Math.min(fill[2], 0.42)], hslToRgb(soft));
  return {
    "--accent": css(fill),
    "--accent-hover": css([h, s, Math.max(fill[2] - 0.06, 0.05)]),
    "--accent-soft": css(soft),
    "--accent-ink": css(ink),
    "--focus-ring": css(fill),
  };
}

export function TenantProvider({ children }) {
  const { data, error, isLoading } = useTenants();
  const { filters } = useFilters();

  const value = useMemo(() => {
    // Only the tenants this user may open (the server filters by permission).
    const tenants = data?.tenants ?? [];
    const key = filters.tenant;
    const current =
      key === ALL_TENANTS
        ? { key: ALL_TENANTS, name: "All tenants", brand: TENANTS[0].brand, modules: null, labels: {} }
        : (tenants.find((t) => t.key === key) ?? getTenant(key));
    return {
      tenants,
      current,
      allowAll: data?.allowAll ?? false,
      loading: isLoading,
      error,
    };
  }, [data, error, isLoading, filters.tenant]);

  const { current } = value;
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", current.brand.primary);
    root.style.setProperty("--brand-accent", current.brand.accent);
    // The chrome's accent follows the brand; without a usable colour the stylesheet's CLIRNET default stays.
    const shades = accentShades(current.brand.accent);
    for (const name of ["--accent", "--accent-hover", "--accent-soft", "--accent-ink", "--focus-ring"]) {
      if (shades) root.style.setProperty(name, shades[name]);
      else root.style.removeProperty(name);
    }
    document.title = `${current.name} · Content Analytics`;
  }, [current]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside TenantProvider");
  return ctx;
}

/** A tab's title for the active tenant: its config name for the module, else the catalog title. */
// eslint-disable-next-line react-refresh/only-export-components
export function tabTitle(tab, current) {
  if (!tab) return "";
  return (tab.module && current.labels?.[tab.module]) || tab.title;
}

/** Whether a catalog tab is available for the active tenant. */
// eslint-disable-next-line react-refresh/only-export-components
export function tabEnabled(tab, current) {
  if (tab.fresh && current.reportsOnly) return false;
  if (tab.platform && !platformsInclude(current.platforms, tab.platform)) return false;
  if (!tab.module || !current.modules) return true;
  return current.modules.includes(tab.module);
}
